import {
  Fragment,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { usePageStore } from '@/store/usePageStore';
import { Button } from '@/components/atoms/Button/Button';
import { IconPlus } from '@/components/atoms/AnimatedIcons/IconPlus/IconPlus';
import { useIsMobile } from '@/hooks/useIsMobile';
import { Typography } from '@/components/atoms/Typography/Typography';
import * as Styled from './FiltersSections.styled';
import {
  IFilter,
  ITabsAndFilterGroups,
  TFilterVisibility,
} from '../../FilteredProjectsGallery/FilteredProjectsGallery.types';
import { useFiltersStore } from '@/store/useFiltersStore';

interface IFilterSections {
  filtersData: ITabsAndFilterGroups;
  pageFilters: IFilter[];
  getButtonColor: (id: string, collection: string) => string;
}

/**
 * Displays the filters divided in groups (sections)
 */
export const FilterSections = ({
  filtersData,
  pageFilters,
  getButtonColor,
}: IFilterSections) => {
  const isMobile = useIsMobile();
  const isUserLogged = usePageStore((s) => s.isUserLogged);
  const refsFilterSections = useRef<(HTMLUListElement | null)[]>([]);
  const selectedFilters = useFiltersStore((state) => state.selectedFilters);
  const areFiltersOpen = useFiltersStore((state) => state.areFiltersOpen);
  const toggleFilter = useFiltersStore((state) => state.toggleFilter);
  const locationFilterNumActive = useFiltersStore((state) => state.locationFilterNumActive);
  const setLocationFilterNumActive = useFiltersStore((state) => state.setLocationFilterNumActive);

  useLayoutEffect(() => {
    if (isMobile) {
      refsFilterSections.current.forEach((ref) => {
        const height = ref?.scrollHeight;
        ref?.style.setProperty('--height-section-mobile', `${height}px`);
      });
    }
  }, [isMobile]);

  const [expandedSections, setExpandedSections] = useState(
    Array(filtersData?.sections?.length || 0).fill(false)
  );

  const isFilterDisplayed = (visibility: TFilterVisibility) => {
    return (
      visibility === 'public' || (visibility === 'private' && isUserLogged)
    );
  };

  /** Toggles the collapsed filters section on mobile, only one section can be expanded at a time
   * @param {number} sectionNum - The section number to toggle
   * @returns {void}
   **/
  const toggleFilterSectionMobile = (sectionNum: number): void => {
    setExpandedSections((prevSections) =>
      prevSections.map((val, i) => (i === sectionNum ? !val : false))
    );
  };

  /** Counts the number of selected filters in a specific section
   * @param {string}   sectionNum  - The number of the section to count matches from
   * @returns {string}             - Number of selected filters
   **/
  const countMatchesBySection = (sectionNum: number): string => {
    const groupFiltersData = filtersData?.sections?.[sectionNum] ?? null;

    if (!groupFiltersData) return '0';
    let matchCount = 0;

    groupFiltersData.filters.forEach((filterData) => {
      // If it is a group filter with subfilters that are displayed, count only the subfilters
      if (
        filterData.subfilters &&
        filterData.subfilters.length > 0 &&
        areSubFiltersDisplayed(filterData.subfilters)
      ) {
        matchCount += filterData.subfilters.filter((sub) =>
          selectedFilters?.filters?.some(
            (selectedFilter) =>
              selectedFilter.id === sub.id &&
              selectedFilter.collection === sub.collection
          )
        ).length;
      } else {
        // There are no subfilters, so count matches in filters
        if (
          selectedFilters?.filters?.some(
            (selectedFilter) =>
              selectedFilter.id === filterData.id &&
              selectedFilter.collection === filterData.collection
          )
        )
          matchCount++;
      }
    });

    return matchCount ? matchCount.toString() : '';
  };

  /* gets the button color based on the selected filters */
  const isButtonSelected = useCallback(
    (filterId: string | number, collection: string) => {
      if (!selectedFilters || !selectedFilters.filters) return false;

      return selectedFilters.filters.some(
        (filter) =>
          Number(filter.id) === Number(filterId) &&
          filter.collection === collection
      );
    },
    [selectedFilters]
  );

  const areSubFiltersDisplayed = (subfilters: IFilter[]): boolean => {
    const areAllSubfiltersPrivate = subfilters.every(
      (subfilter) => subfilter.visibility === 'private'
    );
    return (
      subfilters &&
      (subfilters ?? []).length > 0 &&
      (!areAllSubfiltersPrivate || isUserLogged)
    );
  };

  if (!filtersData || filtersData?.sections?.length === 0) return <></>;
      
  return (filtersData?.sections ?? []).map((section, i) => {
    const sectionKey = `filter-section-${i}`;
    const isSectionExpanded = expandedSections[i];

    const handleSectionToggle = () => toggleFilterSectionMobile(i);
    const sectionMatchCount = countMatchesBySection(i);
    const visibility = section.groupVisibility;
    const isLocationSection = section.filters[0].collection === 'locations_level_1';

    const isSectionDisplayed = visibility === 'open_private'
      || visibility === 'only_private' && isUserLogged
      || visibility === 'only_public' && !isUserLogged;

    if (!isSectionDisplayed) return null;

    const areLocationSubfiltersDesktop = (collection: string) => {
      const isLocationSection = collection === 'locations_level_1';

      return isLocationSection && !isMobile && isUserLogged;
    };

    const areLocationSubfiltersMobile = (collection: string) => {
      return (collection === 'locations_level_2' && isMobile && isUserLogged);
    };

    const isFilterFromPageFilter = (filter: IFilter) => {
      return pageFilters?.some(pageFilter => 
        pageFilter.id === filter.id && pageFilter.collection === filter.collection
      );
    };

    const handleFilterClick = (filter: IFilter, order: number) => {

      // locations_level_1 work as a button to display the sublist only when user is logged in
      if (filter.collection === 'locations_level_1' && isUserLogged) {
        setLocationFilterNumActive(order);
        // if (locationFiltersSelectedFromSubfilters?.includes(order)) {
        //   removeSelectedLocationFromSubfilterNum(order);
        // } else {
        //   addSelectedLocationFromSubfilterNum(order);
        // }
        return;
      }

      toggleFilter(filter);
    }

    const getFilterButtonClasses = (filter: IFilter, order: number) => {

      const classes = [];

      if (isButtonSelected(filter.id, filter.collection)) {
        classes.push('selected');
      } 

      // If the filter is a location_level_1 and the user is logged in, add specific classes
      // to style the button, because list will be in a separate container (3 columns in this case)
      if (isUserLogged && filter.collection === 'locations_level_1') {
        // when list is opened
        if (locationFilterNumActive === order) {
          classes.push('location-filter-opened');
        }

        // when a subfilter is selected
        // if (locationFiltersSelectedFromSubfilters?.includes(order)) {
        //   classes.push('location-filter-with-selected-subfilters');
        // }

        // when a subfilter is selected in the locations_level_2 collection
        if (selectedFilters?.filters?.some(f =>
          f.collection === 'locations_level_2' &&
          filter.subfilters?.some(sub => sub.id === f.id && sub.collection === f.collection)
        )) {
          classes.push('location-subfilter-selected');
        }
      }

      return classes.join(' ');
    };

    return (
      <Styled.Column 
        key={sectionKey} 
        isDisplayed={areFiltersOpen} 
        className={isLocationSection ? 'location-section' : 'not-location'}
        data-filter-type={section.filters[0].collection}
      >
        <Button
          color="secondary"
          className="btn-collapsible-mobile"
          callbackOnClick={handleSectionToggle}
        >
          <IconPlus isExpanded={isSectionExpanded} className="display-only-mobile" />
          <span>
            <Typography as="span" text={section.text} color="secondary" />
            {sectionMatchCount !== '0' && (
              <Typography
                as="span"
                text={sectionMatchCount}
                size="sm"
                color="primary"
                className="filters-count"
              />
            )}
          </span>
        </Button>

        <Styled.FiltersSection
          data-is-expanded={isSectionExpanded}
          ref={(el) => { refsFilterSections.current[i] = el; }}
        >
          {section.filters.map((filter, j) => {
            const subfilters = filter.subfilters ?? [];
            const hasVisibleSubfilters = areSubFiltersDisplayed(subfilters);
            // if filter comes from page filters, disable it only when there are no subfilters
            const isFilterDisabled = isFilterFromPageFilter(filter);

            const renderSubFilters = () =>
              hasVisibleSubfilters && (
                <Styled.ListSubfilters>
                  {subfilters.map((sub, k) => {
                    const isSubVisible = isFilterDisplayed(sub.visibility as TFilterVisibility);
                    const isSelected = isButtonSelected(sub.id, sub.collection);
                    const subKey = `item-subfilter-${k}-${j}-${i}`;

                    return (
                      isSubVisible && (
                        <Styled.FilterItem key={subKey}>
                          <Button
                            text={sub.text}
                            callbackOnClick={() => toggleFilter(sub)}
                            data-btn-color={getButtonColor(sub.id, sub.collection)}
                            className={isSelected ? 'selected' : ''}
                          />
                        </Styled.FilterItem>
                      )
                    );
                  })}
                </Styled.ListSubfilters>
              );

            const collectionFilter = filter.collection;
            const collectionSubfilter = filter.subfilters?.[0]?.collection || '';
            const shouldRenderSubFilters =
              collectionFilter === 'locations_level_1'
                ? areLocationSubfiltersMobile(collectionSubfilter)
                : subfilters.length > 0 && hasVisibleSubfilters;

            if (shouldRenderSubFilters) {
              return (
                <Styled.FiltersSubSection
                  key={`sub-section-${i}-${j}`}
                  className="filters-subsection subsection-test"
                >
                  {isFilterDisplayed(filter.visibility as TFilterVisibility) && (
                    <Styled.FilterItem
                      className="filter-item"
                      data-hidden={isFilterDisplayed(filter.visibility as TFilterVisibility)}
                      as='div'
                    >
                      <Typography
                        text={filter.text}
                        color="secondary"
                        data-btn-color={getButtonColor(filter.id, filter.collection)}
                        className={isButtonSelected(filter.id, filter.collection) ? 'selected' : ''}
                      />
                    </Styled.FilterItem>
                  )}
                  {renderSubFilters()}
                </Styled.FiltersSubSection>
              );
            }

            return (
                <Fragment key={`sub-section-${i}-${j}`}>
                  <Styled.FilterItem
                    hasMarginBottom
                    data-disabled={isFilterDisabled}
                    className="filter-item"
                  >
                    <Button
                      text={filter.text}
                      callbackOnClick={() => handleFilterClick(filter, j)}
                      data-btn-color={getButtonColor(filter.id, filter.collection)}
                      className={getFilterButtonClasses(filter, j)}
                    />
                  </Styled.FilterItem>
                </Fragment>
              )
            })}
        </Styled.FiltersSection>


        {areLocationSubfiltersDesktop(section.filters[0].collection) && (
          <Styled.LocationSubfilters>
            {
              section.filters.map((filter, j) => {
                const subfilters = filter.subfilters ?? [];
                const hasVisibleSubfilters = areSubFiltersDisplayed(subfilters);
                const isFilterDisabled = isFilterFromPageFilter(filter);

                if (hasVisibleSubfilters && j === locationFilterNumActive) {
                  return (
                    <Styled.ListSubfilters key={`location-subfilter-${j}`} as='ul'>
                      {subfilters.map((sub, k) => {
                        const isSelected = isButtonSelected(sub.id, sub.collection);
                        const subKey = `item-location-subfilter-${k}-${j}`;

                        return (
                          <Styled.FilterItem key={subKey} data-disabled={isFilterDisabled}>
                            <Button
                              text={sub.text}
                              callbackOnClick={() => toggleFilter(sub)}
                              data-btn-color={getButtonColor(sub.id, sub.collection)}
                              className={isSelected ? 'selected' : ''}
                            />
                          </Styled.FilterItem>
                        );
                      })}
                    </Styled.ListSubfilters>
                  );
                }
                return null;
              }
            )}
          </Styled.LocationSubfilters>
        )}
      </Styled.Column>
    );
  });
};
